import React from "react";
import type {
  AgentOsExecutionPolicyView,
  AgentOsHistoryRunView,
  AgentOsMonitoringView,
  AgentOsReplayView,
  AgentOsRuleView,
  AgentOsRunEventView,
  AgentOsRunView,
  AgentOsSettingsView,
} from "../api";
import { Panel } from "./Panels";

type AgentOsLastRun = Pick<AgentOsRunView, "id" | "status" | "phase" | "model"> & {
  executionPolicy: AgentOsExecutionPolicyView;
};

function PolicyPill({ ok, children }: { ok: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <span className={`rounded border px-2 py-1 text-[10px] uppercase tracking-[0.14em] ${ok ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100" : "border-amber-400/40 bg-amber-400/10 text-amber-100"}`}>
      {children}
    </span>
  );
}

function ModeCard({ mode, active }: { mode: AgentOsSettingsView["autonomyModes"][number]; active: boolean }): JSX.Element {
  return (
    <div className={`rounded-md border p-2 ${active ? "border-jarvis-accent/60 bg-jarvis-accent/12" : "border-jarvis-border/55 bg-jarvis-bg/35"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="truncate text-xs font-semibold text-jarvis-text" title={mode.label}>{mode.label}</div>
        <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{mode.permissionFloor}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] text-jarvis-muted">
        <span>Tools</span><span className="truncate text-right" title={mode.toolPolicy}>{mode.toolPolicy}</span>
        <span>Approval</span><span className="truncate text-right" title={mode.approvalThreshold}>{mode.approvalThreshold}</span>
      </div>
    </div>
  );
}

function LifecycleDots({ run }: { run: AgentOsHistoryRunView }): JSX.Element {
  const items: Array<[keyof AgentOsHistoryRunView["lifecycle"], string]> = [
    ["plan", "Plan"],
    ["worktree", "Worktree"],
    ["diff", "Diff"],
    ["checks", "Checks"],
    ["approval", "Approval"],
    ["pr", "PR"],
    ["merge", "Merge"],
    ["safeTag", "Safe-Tag"],
    ["evidence", "Evidence"],
  ];
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {items.map(([key, label]) => (
        <span
          key={key}
          className={`rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em] ${run.lifecycle[key] ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100" : "border-jarvis-border/45 bg-jarvis-bg/40 text-jarvis-muted"}`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function textField(value: unknown, fallback = "-"): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : fallback;
}

function fieldValue(record: Record<string, unknown> | null | undefined, key: string): unknown {
  return record ? record[key] : undefined;
}

function textList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => textField(item, "")).filter((item) => item.length > 0);
}

function EventList({ title, events, empty }: { title: string; events: AgentOsRunEventView[]; empty: string }): JSX.Element {
  return (
    <div className="min-w-0 rounded-md border border-jarvis-border/50 bg-jarvis-surface/35 p-2">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">{title}</div>
        <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{events.length}</span>
      </div>
      <div className="h-28 overflow-auto pr-1 simo-thin-scroll">
        {events.length === 0 ? (
          <div className="text-xs text-jarvis-muted">{empty}</div>
        ) : events.map((event, index) => (
          <div key={`${event.at}-${event.type}-${index}`} className="mb-1 min-w-0 rounded border border-jarvis-border/35 bg-jarvis-bg/35 px-2 py-1 text-xs">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-jarvis-accent">{event.type}</span>
              <span className="truncate text-[10px] text-jarvis-muted" title={event.at}>{event.at}</span>
            </div>
            <div className="mt-1 break-words text-jarvis-text">{event.summary}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AgentOsMissionConsolePanel({
  settings,
  rules,
  lastRun,
  history,
  historyPagination,
  selectedReplay,
  liveEvents,
  monitoring,
  historyQuery,
  historyStatus,
  error,
  onRefresh,
  onModeChange,
  onSettingsChange,
  onCreateRun,
  onSelectRun,
  onHistoryQueryChange,
  onHistoryStatusChange,
  onLoadMoreHistory,
}: {
  settings: AgentOsSettingsView | null;
  rules: AgentOsRuleView[];
  lastRun: AgentOsLastRun | null;
  history: AgentOsHistoryRunView[];
  historyPagination: { limit: number; offset: number; total: number; hasMore: boolean } | null;
  selectedReplay: AgentOsReplayView | null;
  liveEvents: AgentOsRunEventView[];
  monitoring: AgentOsMonitoringView | null;
  historyQuery: string;
  historyStatus: "all" | AgentOsRunView["status"];
  error: string | null;
  onRefresh: () => void | Promise<void>;
  onModeChange: (mode: AgentOsSettingsView["activeMode"]) => void | Promise<void>;
  onSettingsChange: (patch: Partial<Pick<AgentOsSettingsView, "clarificationPolicy" | "reads" | "changes" | "modelDefaults">>) => void | Promise<void>;
  onCreateRun: (expectedAction: "read" | "write") => void | Promise<void>;
  onSelectRun: (runId: string) => void | Promise<void>;
  onHistoryQueryChange: (query: string) => void | Promise<void>;
  onHistoryStatusChange: (status: "all" | AgentOsRunView["status"]) => void | Promise<void>;
  onLoadMoreHistory: () => void | Promise<void>;
}): JSX.Element {
  const activeMode = settings?.autonomyModes.find((mode) => mode.id === settings.activeMode) ?? null;
  const visualRule = rules.find((rule) => rule.id === "visual-collaboration-real-mockups") ?? null;
  const selectedPrNumber = textField(selectedReplay?.pr?.number, "");
  const selectedSafeTag = textField(selectedReplay?.safeTag?.name, "");
  const selectedDiffFiles = Array.isArray(selectedReplay?.diff?.files) ? selectedReplay.diff.files.slice(0, 4) : [];
  const selectedDiffRaw = textField(fieldValue(selectedReplay?.diff, "raw"), "");
  const selectedPromptText = selectedReplay?.prompt.text ?? selectedReplay?.prompt.summary ?? "";
  const selectedPlanFiles = textList(fieldValue(selectedReplay?.plan, "files")).slice(0, 4);
  const selectedPlanCommands = textList(fieldValue(selectedReplay?.plan, "testCommands")).slice(0, 3);
  const selectedWorktreeBranch = textField(fieldValue(selectedReplay?.worktree, "branch"), "");
  const selectedWorktreePath = textField(fieldValue(selectedReplay?.worktree, "path"), "");
  const selectedChecksStatus = textField(selectedReplay?.checks?.status, "");
  const selectedChecksCommands = textList(fieldValue(selectedReplay?.checks, "commands")).slice(0, 3);
  const selectedMergeCommit = textField(selectedReplay?.merge?.commit, "");
  const selectedEvidenceSummary = textField(selectedReplay?.evidence?.summary, "");
  const selectedMissing = selectedReplay?.completeness.missing ?? [];
  const selectedTimeline = selectedReplay?.timeline.slice(-5) ?? [];
  const selectedEvidenceLedger = selectedReplay?.evidenceLedger.slice(-3) ?? [];
  const historyCountLabel = historyPagination ? `${history.length} / ${historyPagination.total}` : String(history.length);
  const selectedPromptAvailability = selectedReplay?.prompt.rawAvailable
    ? "raw prompt available"
    : selectedReplay?.prompt.redaction === "secret_like_values_redacted"
      ? "prompt redacted for secrets"
      : "raw prompt unavailable";
  const terminalEvents = liveEvents.filter((event) => event.type === "terminal_line" || event.type === "tool_call" || event.type === "tool_result");
  const diffEvents = liveEvents.filter((event) => event.type === "diff_snapshot");
  const checkEvents = liveEvents.filter((event) => event.type === "check_started" || event.type === "check_finished");
  const approvalEvents = liveEvents.filter((event) => event.type === "approval_required");
  const monitoringAlerts = monitoring?.alerts.slice(0, 5) ?? [];

  return (
    <Panel
      title="SimoT Agent OS"
      accent={error ? "simo-accent-warn" : lastRun?.status === "awaiting_approval" ? "simo-accent-confirming" : "simo-accent-ok"}
      action={<button type="button" className="topbar-control min-h-[32px] px-2" onClick={() => void onRefresh()} aria-label="Mission-Konsole aktualisieren">Aktualisieren</button>}
    >
      {error ? <div className="mb-3 rounded-md border border-rose-500/35 bg-rose-950/30 px-2 py-1.5 text-xs text-rose-100">{error}</div> : null}
      <div className="grid gap-3 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-3">
          <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/35 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-jarvis-text">Mission Console</div>
                <div className="mt-1 text-xs text-jarvis-muted">{settings?.primaryRuntime ?? "ollama:gex44"} · {activeMode?.label ?? "Autonomous Gated"}</div>
              </div>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{settings?.layout ?? "mission_console"}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <PolicyPill ok={settings?.reads.promptBeforeRead === false}>Reads ohne Prompts</PolicyPill>
              <PolicyPill ok={settings?.changes.requireApproval !== false}>Changes approval-gated</PolicyPill>
              <PolicyPill ok>Shell/Git/Deploy gated</PolicyPill>
              <PolicyPill ok={settings?.changes.diffPreviewRequired !== false}>Diff Preview</PolicyPill>
              <PolicyPill ok={settings?.changes.worktreeRequired !== false}>Worktree</PolicyPill>
            </div>
          </div>

          <div className="grid gap-2 md:grid-cols-3">
            {(settings?.autonomyModes ?? []).map((mode) => (
              <button key={mode.id} type="button" className="text-left" onClick={() => void onModeChange(mode.id)}>
                <ModeCard mode={mode} active={settings?.activeMode === mode.id} />
              </button>
            ))}
          </div>

          <div className="simo-keyboard-safe-editor rounded-md border border-jarvis-border/60 bg-jarvis-bg/30 p-3" aria-label="Agent OS Session History Keyboard-safe Filters">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Run Loop</div>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">gated</span>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <button type="button" className="topbar-control text-left" onClick={() => void onCreateRun("read")}>Research Run</button>
              <button type="button" className="topbar-control text-left" onClick={() => void onCreateRun("write")}>Plan Change</button>
            </div>
            {lastRun ? (
              <div className="mt-3 grid gap-2 rounded-md border border-jarvis-border/45 bg-jarvis-surface/45 p-2 text-xs text-jarvis-muted md:grid-cols-4">
                <span className="truncate font-mono text-jarvis-text" title={lastRun.id}>{lastRun.id}</span>
                <span>{lastRun.status}</span>
                <span>{lastRun.phase}</span>
                <span className="truncate" title={lastRun.model ?? ""}>{lastRun.model ?? "auto"}</span>
              </div>
            ) : null}
          </div>

          <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/30 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Run Monitoring</div>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{monitoring?.requestId ?? "not loaded"}</span>
            </div>
            {monitoring ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {[
                    ["running", monitoring.counts.running],
                    ["awaiting", monitoring.counts.awaitingApproval],
                    ["errors", monitoring.counts.error],
                    ["alerts", monitoring.alerts.length],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">{label}</div>
                      <div className="mt-1 font-mono text-lg text-jarvis-text">{value}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  <PolicyPill ok={monitoring.gates.executionAllowed === false}>read-only</PolicyPill>
                  <PolicyPill ok={monitoring.gates.sendsNotifications === false}>no notification send</PolicyPill>
                  <PolicyPill ok={monitoring.gates.createsRunnerJobs === false}>no runner jobs</PolicyPill>
                  <PolicyPill ok={monitoring.gates.mutatesRuns === false}>no run mutation</PolicyPill>
                </div>
                <div className="max-h-44 space-y-1 overflow-auto pr-1 simo-thin-scroll">
                  {monitoringAlerts.length === 0 ? (
                    <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 p-2 text-xs text-jarvis-muted">No Agent OS alerts.</div>
                  ) : monitoringAlerts.map((alert) => (
                    <button
                      key={alert.id}
                      type="button"
                      className="w-full rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2 text-left"
                      onClick={() => void onSelectRun(alert.runId)}
                    >
                      <div className="flex min-w-0 items-center justify-between gap-2">
                        <span className="truncate text-xs font-semibold text-jarvis-text" title={alert.title}>{alert.title}</span>
                        <span className={`badge ${alert.severity === "critical" ? "bg-rose-400/10 text-rose-100" : alert.severity === "warning" ? "bg-amber-400/10 text-amber-100" : "bg-jarvis-bg/60 text-jarvis-muted"}`}>
                          {alert.severity}
                        </span>
                      </div>
                      <div className="mt-1 break-words text-xs text-jarvis-muted">{alert.summary}</div>
                      <div className="mt-1 flex flex-wrap gap-1 font-mono text-[10px] text-jarvis-muted">
                        <span>{alert.evidenceRef}</span>
                        <span>{alert.safeActions.join(", ")}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 p-2 text-xs text-jarvis-muted">Monitoring wird geladen.</div>
            )}
          </div>

          <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/30 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Live Run Stream</div>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{liveEvents.length} events</span>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <EventList title="Stream" events={liveEvents} empty="No stream events yet." />
              <EventList title="Terminal" events={terminalEvents} empty="No terminal/tool lines." />
              <EventList title="Diff" events={diffEvents} empty="No diff snapshot." />
              <EventList title="Checks" events={checkEvents} empty="No checks recorded." />
            </div>
            <div className="mt-2">
              <EventList title="Approval" events={approvalEvents} empty={lastRun?.executionPolicy.approvalRequired ? "Approval event pending." : "No approval required."} />
            </div>
          </div>

          <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/30 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Session History</div>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{historyCountLabel}</span>
            </div>
            <div className="mb-2 grid gap-2 md:grid-cols-[minmax(0,1fr)_130px]">
              <input
                aria-label="Search session history"
                className="simo-keyboard-safe-field min-h-11 rounded-md border border-jarvis-border/55 bg-jarvis-surface/45 px-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/70 sm:text-xs"
                placeholder="Search run, prompt, PR, safe-tag, evidence"
                value={historyQuery}
                onChange={(event) => void onHistoryQueryChange(event.currentTarget.value)}
              />
              <select
                aria-label="Session history status"
                className="simo-keyboard-safe-field min-h-11 rounded-md border border-jarvis-border/55 bg-jarvis-surface/45 px-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/70 sm:text-xs"
                value={historyStatus}
                onChange={(event) => void onHistoryStatusChange(event.currentTarget.value as "all" | AgentOsRunView["status"])}
              >
                <option value="all">all</option>
                <option value="done">done</option>
                <option value="awaiting_approval">awaiting</option>
                <option value="running">running</option>
                <option value="error">error</option>
                <option value="cancelled">cancelled</option>
              </select>
            </div>
            <div className="max-h-80 space-y-2 overflow-auto pr-1 simo-thin-scroll">
              {history.length === 0 ? (
                <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 p-2 text-xs text-jarvis-muted">Noch keine replaybaren Agent-OS-Runs.</div>
              ) : history.map((run) => (
                <button
                  key={run.id}
                  type="button"
                  className={`w-full rounded-md border p-2 text-left ${selectedReplay?.run.id === run.id ? "border-jarvis-accent/60 bg-jarvis-accent/12" : "border-jarvis-border/45 bg-jarvis-surface/40"}`}
                  onClick={() => void onSelectRun(run.id)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs text-jarvis-text" title={run.id}>{run.id}</div>
                      <div className="mt-1 truncate text-xs text-jarvis-muted" title={run.prompt.summary}>{run.prompt.summary}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{run.status}</span>
                      <span className={`badge ${run.completeness.complete ? "bg-emerald-400/10 text-emerald-100" : "bg-amber-400/10 text-amber-100"}`}>
                        {run.completeness.complete ? "complete" : `${run.completeness.missing.length} missing`}
                      </span>
                      {run.pr ? <span className="badge bg-jarvis-bg/60 text-jarvis-muted">PR #{textField(run.pr.number)}</span> : null}
                    </div>
                  </div>
                  <LifecycleDots run={run} />
                </button>
              ))}
              {historyPagination?.hasMore ? (
                <button
                  type="button"
                  className="topbar-control w-full justify-center text-xs"
                  onClick={() => void onLoadMoreHistory()}
                >
                  Load more
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="simo-keyboard-safe-editor rounded-md border border-jarvis-border/60 bg-jarvis-bg/35 p-3" aria-label="Agent OS Runtime Center Keyboard-safe Settings">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Runtime Center</div>
              <span className="badge bg-emerald-400/10 text-emerald-100">ollama-only</span>
            </div>
            {settings ? (
              <div className="space-y-3 text-xs text-jarvis-muted">
                <div className="grid gap-2 md:grid-cols-2">
                  <label className="grid gap-1">
                    <span>Primary Runtime</span>
                    <input className="simo-keyboard-safe-field min-h-11 rounded-md border border-jarvis-border/55 bg-jarvis-bg/55 px-2 font-mono text-base text-jarvis-muted sm:text-xs" value={settings.primaryRuntime} disabled readOnly />
                  </label>
                  <label className="grid gap-1">
                    <span>Clarification</span>
                    <select
                      className="simo-keyboard-safe-field min-h-11 rounded-md border border-jarvis-border/55 bg-jarvis-surface/45 px-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/70 sm:text-xs"
                      value={settings.clarificationPolicy}
                      onChange={(event) => void onSettingsChange({ clarificationPolicy: event.currentTarget.value as AgentOsSettingsView["clarificationPolicy"] })}
                    >
                      <option value="minimal">minimal</option>
                      <option value="balanced">balanced</option>
                      <option value="high_precision">high_precision</option>
                    </select>
                  </label>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {Object.entries(settings.reads).map(([key, value]) => (
                    <label key={key} className="flex min-h-[32px] items-center justify-between gap-2 rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 px-2">
                      <span className="truncate" title={key}>{key}</span>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(event) => void onSettingsChange({ reads: { ...settings.reads, [key]: event.currentTarget.checked } })}
                      />
                    </label>
                  ))}
                  {Object.entries(settings.changes).map(([key, value]) => (
                    <label key={key} className="flex min-h-[32px] items-center justify-between gap-2 rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 px-2">
                      <span className="truncate" title={key}>{key}</span>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(event) => void onSettingsChange({ changes: { ...settings.changes, [key]: event.currentTarget.checked } })}
                      />
                    </label>
                  ))}
                </div>
                <div className="grid gap-2">
                  {Object.entries(settings.modelDefaults).map(([key, value]) => (
                    <label key={key} className="grid gap-1">
                      <span>{key} model</span>
                      <input
                        className="simo-keyboard-safe-field min-h-11 rounded-md border border-jarvis-border/55 bg-jarvis-surface/45 px-2 font-mono text-base text-jarvis-text outline-none focus:border-jarvis-accent/70 sm:text-xs"
                        defaultValue={value}
                        pattern="ollama:.*"
                        title="Only ollama:* model ids are allowed"
                        onBlur={(event) => {
                          const next = event.currentTarget.value.trim();
                          if (next && next !== value) void onSettingsChange({ modelDefaults: { ...settings.modelDefaults, [key]: next } });
                        }}
                      />
                    </label>
                  ))}
                </div>
                <div className="grid gap-1 rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 p-2">
                  <div className="flex items-center justify-between gap-2"><span>Shell/Git/Deploy</span><span className="badge bg-jarvis-bg/60 text-jarvis-muted">locked gated</span></div>
                  <div className="flex items-center justify-between gap-2"><span>Connector/Memory/Rules</span><span className="badge bg-jarvis-bg/60 text-jarvis-muted">locked gated</span></div>
                  <div className="flex items-center justify-between gap-2"><span>Cloud fallback</span><span className="badge bg-rose-400/10 text-rose-100">not available</span></div>
                </div>
              </div>
            ) : <span className="text-xs text-jarvis-muted">Settings werden geladen.</span>}
          </div>

          <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/35 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Operating Rules</div>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{rules.length}</span>
            </div>
            <div className="space-y-2">
              {(visualRule ? [visualRule, ...rules.filter((rule) => rule.id !== visualRule.id)] : rules).slice(0, 4).map((rule) => (
                <div key={rule.id} className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-jarvis-text" title={rule.title}>{rule.title}</span>
                    <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{rule.category}</span>
                  </div>
                  <div className="mt-1 truncate font-mono text-[10px] text-jarvis-muted" title={rule.id}>{rule.id}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/35 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Replay Record</div>
              <div className="flex items-center gap-1">
                {selectedReplay ? (
                  <span className={`badge ${selectedReplay.completeness.complete ? "bg-emerald-400/10 text-emerald-100" : "bg-amber-400/10 text-amber-100"}`}>
                    {selectedReplay.completeness.complete ? "complete" : `${selectedMissing.length} missing`}
                  </span>
                ) : null}
                <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{selectedReplay ? selectedReplay.run.status : "none"}</span>
              </div>
            </div>
            {selectedReplay ? (
              <div className="space-y-2 text-xs text-jarvis-muted">
                {!selectedReplay.completeness.complete ? (
                  <div className="rounded-md border border-amber-400/35 bg-amber-400/10 p-2 text-amber-100">
                    Missing replay artifacts: {selectedMissing.join(", ")}
                  </div>
                ) : null}
                <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                  <div className="line-clamp-3 text-jarvis-text" title={selectedPromptText}>{selectedPromptText}</div>
                  <div className="mt-1 font-mono text-[10px] text-jarvis-muted">{selectedPromptAvailability}</div>
                </div>
                <div className="grid gap-1 md:grid-cols-2">
                  <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Worktree</div>
                    <div className="mt-1 truncate text-jarvis-text" title={selectedWorktreePath || selectedWorktreeBranch}>{selectedWorktreeBranch || "not recorded"}</div>
                  </div>
                  <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Checks</div>
                    <div className="mt-1 truncate text-jarvis-text" title={selectedChecksCommands.join(", ")}>{selectedChecksStatus || "not recorded"}</div>
                  </div>
                  <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">PR</div>
                    <div className="mt-1 truncate text-jarvis-text" title={textField(selectedReplay.pr?.url)}>{selectedPrNumber ? `PR #${selectedPrNumber}` : "not recorded"}</div>
                  </div>
                  <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Merge</div>
                    <div className="mt-1 truncate font-mono text-jarvis-text" title={selectedMergeCommit}>{selectedMergeCommit || "not recorded"}</div>
                  </div>
                  <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Safe-Tag</div>
                    <div className="mt-1 truncate text-jarvis-text" title={selectedSafeTag}>{selectedSafeTag || "not recorded"}</div>
                  </div>
                  <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Evidence</div>
                    <div className="mt-1 truncate text-jarvis-text" title={selectedEvidenceSummary}>{selectedEvidenceSummary || `${selectedReplay.evidenceLedger.length} ledger entries`}</div>
                  </div>
                </div>
                <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                  <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Plan</div>
                  <div className="grid gap-1 font-mono text-[10px]">
                    {selectedPlanFiles.length > 0 ? selectedPlanFiles.map((file) => (
                      <div key={file} className="truncate text-jarvis-text" title={file}>{file}</div>
                    )) : <div className="text-jarvis-muted">No plan files recorded.</div>}
                    {selectedPlanCommands.length > 0 ? (
                      <div className="truncate text-jarvis-muted" title={selectedPlanCommands.join(" && ")}>{selectedPlanCommands.join(" && ")}</div>
                    ) : null}
                  </div>
                </div>
                <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                  <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Diff Files</div>
                  {selectedDiffFiles.length > 0 ? selectedDiffFiles.map((file, index) => (
                    <div key={index} className="flex justify-between gap-2 font-mono text-[10px]">
                      <span className="truncate text-jarvis-text" title={textField((file as { path?: unknown }).path)}>{textField((file as { path?: unknown }).path)}</span>
                      <span>{textField((file as { additions?: unknown }).additions, "0")}+ / {textField((file as { deletions?: unknown }).deletions, "0")}-</span>
                    </div>
                  )) : <div className="text-[10px] text-jarvis-muted">No diff snapshot recorded.</div>}
                  {selectedDiffRaw ? (
                    <pre className="mt-2 max-h-32 overflow-auto rounded border border-jarvis-border/40 bg-jarvis-bg/55 p-2 font-mono text-[10px] leading-4 text-jarvis-text simo-thin-scroll">
                      {selectedDiffRaw}
                    </pre>
                  ) : null}
                </div>
                <div className="grid gap-1">
                  {selectedReplay.approvals.slice(0, 3).map((approval) => (
                    <div key={approval.id} className="flex justify-between gap-2 rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 px-2 py-1">
                      <span className="truncate" title={approval.summary}>{approval.summary}</span>
                      <span className="font-mono text-jarvis-text">{approval.status}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                  <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Timeline</div>
                  <div className="grid gap-1">
                    {selectedTimeline.length > 0 ? selectedTimeline.map((item, index) => (
                      <div key={`${item.kind}-${item.ref ?? index}`} className="grid grid-cols-[72px_minmax(0,1fr)_76px] gap-2 font-mono text-[10px]">
                        <span className="truncate text-jarvis-muted" title={item.at}>{item.at.slice(11, 19) || item.at}</span>
                        <span className="truncate text-jarvis-text" title={item.title}>{item.title}</span>
                        <span className="truncate text-right text-jarvis-muted" title={item.status}>{item.status}</span>
                      </div>
                    )) : <div className="text-[10px] text-jarvis-muted">No timeline events recorded.</div>}
                  </div>
                </div>
                <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/40 p-2">
                  <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Evidence Ledger</div>
                  <div className="grid gap-1">
                    {selectedEvidenceLedger.length > 0 ? selectedEvidenceLedger.map((entry) => (
                      <div key={entry.id} className="flex justify-between gap-2 font-mono text-[10px]">
                        <span className="truncate text-jarvis-text" title={entry.actionId}>{entry.actionId}</span>
                        <span className="text-jarvis-muted">{entry.resultStatus}</span>
                      </div>
                    )) : <div className="text-[10px] text-jarvis-muted">No evidence entries recorded.</div>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 p-2 text-xs text-jarvis-muted">Run aus der Session History auswählen.</div>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}
